/**
 * Remembers what the Restaurants (search) page last showed so that remounting
 * it (browser back/forward, in-app back arrow, switching tabs and returning)
 * brings the list back exactly as the member left it: no card entrance replay
 * and the same scroll position.
 *
 * Why this exists: the search route and the restaurant profile route live
 * under different layouts, so `Search` unmounts when the member opens a
 * profile and every card remounts on the way back. Each card plays an entrance
 * animation on mount (framer `cardVariants` plus the CSS `cardEntrance` in
 * variables.css), so without this the whole list re-staggered in on back
 * navigation, and the scroll position reset to the top.
 *
 * Deliberately history-action agnostic: it does not look at POP vs PUSH or at
 * the view-transition direction, so it behaves the same for the Navigation API
 * path, the popstate path, the hero back arrow and tab-bar returns. A list is
 * "restorable" only while the search identity (term, place type, location,
 * open-now, pages, origin) is unchanged, so a genuinely new search, a filter
 * that triggers a new query, or a first visit still animates.
 *
 * Module state, not React state or storage: it must survive the unmount, must
 * be gone on a full reload (a reload is a fresh load and should animate), and
 * is a handful of ids, so it is never persisted.
 */

/** Inputs that identify "the same search" for restore purposes. */
export interface SearchListIdentity {
  searchTerm: string;
  placeType: string;
  locationPlaceId: string;
  openNow: boolean;
  pages: number | string;
  lat: number;
  lng: number;
}

interface RememberedList {
  key: string;
  ids: ReadonlySet<number>;
  scrollTop: number;
}

let remembered: RememberedList | null = null;

/** Stable string identity for a search. Coordinates are rounded to ~11m so GPS jitter doesn't read as a new search. */
export function buildSearchListKey(identity: SearchListIdentity): string {
  return [
    identity.searchTerm,
    identity.placeType,
    identity.locationPlaceId,
    identity.openNow ? 'open' : 'any',
    identity.pages,
    identity.lat.toFixed(4),
    identity.lng.toFixed(4),
  ].join('|');
}

/** Records the ids currently shown for `key`. Keeps the saved scroll offset only when the search is unchanged. */
export function rememberShownList(key: string, ids: Iterable<number>): void {
  remembered = {
    key,
    ids: new Set(ids),
    scrollTop: remembered?.key === key ? remembered.scrollTop : 0,
  };
}

/** Records the list's scroll offset (no-op until a list has been remembered for `key`). */
export function rememberListScroll(key: string, scrollTop: number): void {
  if (remembered?.key === key) remembered.scrollTop = scrollTop;
}

/**
 * Ids of cards that were already on screen for this same search, or null when
 * there is nothing to restore (first visit, new search). Cards in the set
 * skip their entrance animation; every other card plays it.
 */
export function getRestorableIds(key: string): ReadonlySet<number> | null {
  return remembered?.key === key && remembered.ids.size > 0 ? remembered.ids : null;
}

/** Saved scroll offset for this same search, or 0. */
export function getRestorableScroll(key: string): number {
  return remembered?.key === key ? remembered.scrollTop : 0;
}

/** Test-only reset (and for logout if a caller ever needs to drop the previous member's list). */
export function clearRememberedList(): void {
  remembered = null;
}
